import { useState, type FormEvent } from 'react';
import { DollarSign, Plus, Search, Filter, Trash2, Edit, MapPin, X } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatRelativeTime, cn } from '../utils/formatters';
import { CardSkeleton } from '../components/ui/LoadingScreen';
import { btnGhost, input, label, modalOverlay, modalContent, modalHeader, modalBody, modalFooter, formGroup } from '../styles/components';
import type { Expense } from '../types';

type ExpenseFormState = {
  projectId: string;
  category: Expense['category'];
  description: string;
  amount: string;
  vatRate: string;
  date: string;
  supplier: string;
  notes: string;
};

const emptyForm: ExpenseFormState = {
  projectId: '',
  category: 'material',
  description: '',
  amount: '',
  vatRate: '21',
  date: '',
  supplier: '',
  notes: '',
};

export function ExpensesPage() {
  const { expenses, projects, createExpense, updateExpense, deleteExpense, isLoading } = useCompany();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<ExpenseFormState>(emptyForm);

  const canEdit = user?.role === 'admin' || user?.role === 'superadmin';

  const filteredExpenses = expenses.filter(exp =>
    exp.description.toLowerCase().includes(search.toLowerCase()) ||
    (exp.supplier?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
    (exp.projectId ? (projects.find(p => p.id === exp.projectId)?.name.toLowerCase().includes(search.toLowerCase()) ?? false) : false)
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (exp: Expense) => {
    setEditingId(exp.id);
    setForm({
      projectId: exp.projectId || '',
      category: exp.category,
      description: exp.description || '',
      amount: exp.amount.toString(),
      vatRate: exp.vatRate.toString(),
      date: exp.date ? exp.date.split('T')[0] : '',
      supplier: exp.supplier || '',
      notes: exp.notes || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data = {
        projectId: form.projectId,
        category: form.category,
        description: form.description,
        amount: parseFloat(form.amount) || 0,
        vatRate: parseInt(form.vatRate) || 21,
        date: form.date || new Date().toISOString().split('T')[0],
        supplier: form.supplier,
        notes: form.notes,
      };
      if (editingId) {
        await updateExpense(editingId, data);
      } else {
        await createExpense(data);
      }
      closeModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('¿Eliminar este gasto? Esta acción no se puede deshacer.')) {
      await deleteExpense(id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-extrabold tracking-tight">Gastos</h1>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => <CardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="space-y-1">
          <span className="b2b-eyebrow">Gastos</span>
          <h1 className="text-3xl font-extrabold tracking-tight">Gastos</h1>
          <p className="b2b-muted">Control de compras y gastos con captura móvil</p>
        </div>
        {canEdit && (
          <button onClick={openCreate} className="b2b-btn b2b-btn--primary">
            <Plus className="w-4 h-4" />
            Nuevo Gasto
          </button>
        )}
      </div>

      <div className="b2b-panel">
        <div className="b2b-panel__head">
          <h2 className="text-lg font-semibold">Listado</h2>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 b2b-muted" />
              <input
                type="text"
                placeholder="Buscar..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={cn(input, 'pl-9 w-48')}
              />
            </div>
            <button className="b2b-btn b2b-btn--ghost b2b-btn--sm" type="button">
              <Filter className="w-4 h-4" />
              Filtros
            </button>
          </div>
        </div>

        {filteredExpenses.length === 0 ? (
          <div className="p-12 text-center">
            <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-semibold mb-4">No hay gastos</h3>
            {canEdit && (
              <button onClick={openCreate} className="b2b-btn b2b-btn--primary">
                <Plus className="w-4 h-4" /> Crear gasto
              </button>
            )}
          </div>
        ) : (
          filteredExpenses.map(exp => {
            const project = projects.find(p => p.id === exp.projectId);
            const vatLabel = exp.vatRate === 10 ? 'IVA 10%' : 'IVA 21%';
            return (
              <div key={exp.id} className="b2b-panel__row">
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-3">
                    <span className="font-medium truncate">{exp.description || '—'}</span>
                    <span className="b2b-pill">{vatLabel}</span>
                  </span>
                  <span className="flex items-center gap-2 mt-1 text-sm b2b-muted">
                    <MapPin className="w-3 h-3" />
                    {project?.name || 'Sin proyecto'}
                  </span>
                  <span className="block text-xs b2b-muted mt-1">{formatCurrency(exp.total)} · {formatRelativeTime(exp.date)}</span>
                </span>
                <span className="font-semibold whitespace-nowrap">{formatCurrency(exp.total)}</span>
                {canEdit && (
                  <span className="flex items-center gap-1">
                    <button onClick={() => openEdit(exp)} className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]" aria-label="Editar">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(exp.id)} className="p-2 rounded-lg text-[color:var(--b2b-danger)] hover:bg-[var(--b2b-surface-muted)]" aria-label="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      {showModal && (
        <div className={modalOverlay} onClick={closeModal} role="dialog" aria-modal="true" aria-labelledby="expense-modal-title">
          <div className={cn(modalContent, 'w-full max-w-2xl')} onClick={e => e.stopPropagation()}>
            <div className={modalHeader}>
              <h2 id="expense-modal-title" className="text-lg font-semibold">{editingId ? 'Editar gasto' : 'Nuevo Gasto'}</h2>
              <button type="button" onClick={closeModal} className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className={modalBody}>
                <div className="space-y-3">
                  <div className={formGroup}>
                    <label className={label}>Proyecto *</label>
                    <select className={input} value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })} required>
                      <option value="">Seleccionar proyecto</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className={formGroup}>
                      <label className={label}>Categoría</label>
                      <select className={input} value={form.category} onChange={e => setForm({ ...form, category: e.target.value as Expense['category'] })}>
                        <option value="material">Materiales</option>
                        <option value="tool">Herramientas</option>
                        <option value="transport">Transporte</option>
                        <option value="subcontract">Subcontratación</option>
                        <option value="other">Otros</option>
                      </select>
                    </div>
                    <div className={formGroup}>
                      <label className={label}>Proveedor</label>
                      <input type="text" className={input} value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} />
                    </div>
                  </div>

                  <div className={formGroup}>
                    <label className={label}>Descripción *</label>
                    <input type="text" className={input} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required />
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className={formGroup}>
                      <label className={label}>Importe (sin IVA) *</label>
                      <input type="number" className={input} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} min="0" step="0.01" required />
                    </div>
                    <div className={formGroup}>
                      <label className={label}>IVA</label>
                      <select className={input} value={form.vatRate} onChange={e => setForm({ ...form, vatRate: e.target.value })}>
                        <option value="21">21% (General)</option>
                        <option value="10">10% (Rehabilitación)</option>
                      </select>
                    </div>
                    <div className={formGroup}>
                      <label className={label}>Fecha</label>
                      <input type="date" className={input} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
                    </div>
                  </div>

                  <div className={formGroup}>
                    <label className={label}>Notas</label>
                    <textarea className={cn(input, 'min-h-[80px]')} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className={modalFooter}>
                <button type="button" onClick={closeModal} className={btnGhost}>Cancelar</button>
                <button type="submit" className="b2b-btn b2b-btn--primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Guardando...' : (editingId ? 'Actualizar' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
